@if (
  {
    changeOrder: changeOrder$ | async,
    loading: loading$ | async,
    organization: organization$ | async,
    isPendingReview: isPendingReview$ | async,
    isPendingApproval: isPendingApproval$ | async,
    isApproved: isApproved$ | async,
    isDeclined: isDeclined$ | async,
    isApproveDisabled:
      (isApproveButtonDisable$ | async) ||
      (isApproveButtonDisableManually$ | async) ||
      hasMissingActivitiesWithActuals ||
      hasMissingActivitiesWithMapping,
    isUploadCODisabled: (isUploadCOBudgetButtonDisable$ | async),
    hasUploadCOPermission: (hasUploadCOPermission$ | async),
  };
  as obj
) {
  @if (obj.loading) {
    <div class="spinner m-auto mt-40 h-32 w-32 border-8"></div>
  }

  @if (!obj.loading) {
    @if (obj.changeOrder) {
      <div class="mb-6 flex items-center text-aux-blue-light-200" [routerLink]="[changeOrdersLink]">
        <aux-icon name="ArrowRight" class="mr-2 rotate-180 transform cursor-pointer" />
        <div class="cursor-pointer">Back to Change Orders</div>
      </div>

      <div class="mb-4 flex items-center justify-between">
        <div class="text-xl font-bold">
          {{ obj.organization?.name }} - Change Order {{ obj.changeOrder.change_order_no }}
        </div>
        <div>
          @if (obj.isPendingReview) {
            <aux-button icon="Pencil" label="Edit" (clickEmit)="onEditChangeOrder()" />
          }
        </div>
      </div>

      <div
        class="border px-4"
        [ngClass]="{
          'bg-aux-blue-light-50 text-aux-black': obj.isPendingReview || obj.isPendingApproval,
          'bg-aux-green-dark': obj.isApproved,
          'bg-aux-red-dark pb-4': obj.isDeclined,
          'text-white': obj.isApproved || obj.isDeclined,
        }"
      >
        <div class="flex h-16 items-center justify-between">
          <div>
            <span>Status:</span>
            <span class="ml-2 font-bold">{{ status$ | async }}</span>
            @if (approvedBy$ | async; as approvedBy) {
              <span class="font-bold">
                {{ approvedBy }}
              </span>
            }
          </div>
          <div class="flex items-center space-x-2">
            @if (obj.isPendingReview) {
              <aux-button
                variant="negative"
                [disabled]="isChangeOrdersWorkflowLocked()"
                [auxTooltip]="deleteBtnTooltip(isChangeOrdersWorkflowLocked())"
                (clickEmit)="onDelete()"
                [loading]="isBtnLoading('delete') | async"
                label="Delete"
              />
            }

            @if (obj.isPendingApproval && (showApprovalReminderLink$ | async)) {
              <aux-button
                variant="hyperlink"
                classList="btn-nr"
                (clickEmit)="onSendingPendingApprovalReminder()"
                label="Resend Reminder Email"
              />
            }
            <aux-button
              variant="secondary"
              (clickEmit)="onDownloadCO()"
              [loading]="isBtnLoading('download') | async"
              label="Download CO"
            />
            @if (obj.isPendingReview) {
              <aux-button
                variant="secondary"
                (clickEmit)="onDownloadLREBudget()"
                [loading]="isBtnLoading('lre-download') | async"
                label="Download LRE Budget"
              />

              @if (
                approveBtnTooltip(
                  (userHasApprovePermission$ | async) === true,
                  isChangeOrdersWorkflowLocked(),
                  obj.isApproveDisabled
                );
                as errors
              ) {
                <aux-button
                  variant="secondary"
                  (clickEmit)="onApprove(true)"
                  [disabled]="
                    obj.isApproveDisabled ||
                    (userHasApprovePermission$ | async) === false ||
                    isChangeOrdersWorkflowLocked()
                  "
                  label="Force Approve"
                  auxTooltip
                  [template]="errors.length ? forceApproveErrorTooltip : undefined"
                />
                <ng-template #forceApproveErrorTooltip>
                  <div class="rounded-[4px] py-2 pl-6 pr-4 text-white">
                    <p>{{ errors[0] }}</p>
                    @if (hasMissingActivitiesWithMapping) {
                      <div>
                        <ul class="list-disc">
                          <li>Invoices: {{ invoicesWithMissedMapping }}</li>
                          <li>Activities: {{ activitiesWithMissedMapping }}</li>
                        </ul>
                      </div>
                    }
                  </div>
                </ng-template>
              }
              @if (sendForApprovalBtnTooltip(obj.isApproveDisabled); as errors) {
                <aux-button
                  variant="success"
                  [disabled]="obj.isApproveDisabled"
                  (clickEmit)="onSendApproval()"
                  [loading]="isBtnLoading('approval') | async"
                  label="Send for Approval"
                  auxTooltip
                  [template]="errors.length ? sendForApprovalErrorTooltip : undefined"
                />
                <ng-template #sendForApprovalErrorTooltip>
                  <div class="rounded-[4px] py-2 pl-6 pr-4 text-white">
                    <p>{{ errors[0] }}</p>
                    @if (hasMissingActivitiesWithMapping) {
                      <div>
                        <ul class="list-disc">
                          <li>Invoices: {{ invoicesWithMissedMapping }}</li>
                          <li>Activities: {{ activitiesWithMissedMapping }}</li>
                        </ul>
                      </div>
                    }
                  </div>
                </ng-template>
              }
            }

            @if (obj.isPendingApproval) {
              <aux-button
                variant="secondary"
                (clickEmit)="onReturnToAdminReview()"
                [loading]="isBtnLoading('admin-review') | async"
                label="Return to Admin Review"
              />
              @if (
                approveBtnTooltip(
                  (userHasApprovePermission$ | async) === true,
                  isChangeOrdersWorkflowLocked(),
                  obj.isApproveDisabled
                );
                as errors
              ) {
                <aux-button
                  variant="success"
                  (clickEmit)="onApprove()"
                  [disabled]="
                    obj.isApproveDisabled ||
                    (userHasApprovePermission$ | async) === false ||
                    isChangeOrdersWorkflowLocked()
                  "
                  label="Approve"
                  [loading]="isBtnLoading('approve') | async"
                  auxTooltip
                  [template]="errors.length ? approveErrorTooltip : undefined"
                />
                <ng-template #approveErrorTooltip>
                  <div class="rounded-[4px] py-2 pl-6 pr-4 text-white">
                    <p>{{ errors[0] }}</p>
                    @if (hasMissingActivitiesWithMapping) {
                      <div>
                        <ul class="list-disc">
                          <li>Invoices: {{ invoicesWithMissedMapping }}</li>
                          <li>Activities: {{ activitiesWithMissedMapping }}</li>
                        </ul>
                      </div>
                    }
                  </div>
                </ng-template>
              }
              <aux-button
                variant="negative"
                (clickEmit)="onDecline()"
                [disabled]="(userHasApprovePermission$ | async) === false"
                [auxTooltip]="
                  (userHasApprovePermission$ | async) === false
                    ? messagesConstants.DO_NOT_HAVE_PERMISSIONS_TO_ACTION
                    : ''
                "
                [loading]="isBtnLoading('decline') | async"
                label="Decline"
              />
              <aux-button
                variant="negative"
                [disabled]="isChangeOrdersWorkflowLocked()"
                [auxTooltip]="deleteBtnTooltip(isChangeOrdersWorkflowLocked())"
                (clickEmit)="onDelete()"
                [loading]="isBtnLoading('delete') | async"
                label="Delete"
              />
            }
          </div>
        </div>

        @if (obj.isDeclined) {
          <div class="mt-4 bg-aux-gray-light p-2 text-aux-black">
            <div class="font-bold">Reason for Decline</div>
            <div>{{ obj.changeOrder.decline_reason }}</div>
          </div>
        }
      </div>

      <hr class="mb-3 mt-6" />

      <div class="grid grid-cols-2 gap-5 tabular-nums">
        <div>
          <div class="grid grid-cols-2">
            <div>
              <div class="mb-1 font-bold">Vendor Contract</div>
              <div>{{ obj.organization?.name || zeroHyphen }}</div>
            </div>
            <div>
              <div class="mb-1 font-bold">Change Order #</div>
              <div>{{ obj.changeOrder.change_order_no || zeroHyphen }}</div>
            </div>
          </div>
          <hr class="my-4" />
          <div class="grid grid-cols-2">
            <div>
              <div class="mb-1 font-bold">Effective Date</div>
              <div>{{ obj.changeOrder.effective_date || zeroHyphen }}</div>
            </div>
            <div>
              <div class="mb-1 font-bold">Date Received</div>
              <div>{{ obj.changeOrder.date_received || zeroHyphen }}</div>
            </div>
          </div>
        </div>
        <div>
          <div class="mb-1 font-bold">Change Order Notes</div>
          <div class="whitespace-pre-line">
            {{ obj.changeOrder.notes || zeroHyphen }}
          </div>
        </div>
      </div>
      <hr class="mt-3" />

      <div class="my-6 flex gap-[15px]">
        <div
          class="flex h-[75px] w-[200px] flex-col justify-between rounded-[8px] border border-[#E9EAEE] p-[10px]"
        >
          <div class="text-sm text-aux-gray-dark-100">Activities w/ Increased Cost</div>
          <div class="text-2xl font-bold text-aux-gray-darkest">
            {{ analyticsCard().co_count_activities_with_increased_cost || zeroHyphen }}
          </div>
        </div>
        <div
          class="flex h-[75px] w-[200px] flex-col justify-between rounded-[8px] border border-[#E9EAEE] p-[10px]"
        >
          <div class="text-sm text-aux-gray-dark-100">Activities w/ Increased Units</div>
          <div class="text-2xl font-bold text-aux-gray-darkest">
            {{ analyticsCard().co_count_activities_with_increased_units || zeroHyphen }}
          </div>
        </div>
        <div
          class="flex h-[75px] w-[200px] flex-col justify-between rounded-[8px] border border-[#E9EAEE] p-[10px]"
        >
          <div class="text-sm text-aux-gray-dark-100">Total Cost Impact</div>
          <div class="text-2xl font-bold text-aux-gray-darkest">
            {{ analyticsCard().variance_total_cost_AC || zeroHyphen }}
          </div>
        </div>
      </div>

      <aux-progress-tracker
        [trackerId]="changeOrderBudgetTemplateUploadProgressId()"
        (trackerIdChanged)="onTrackerIdChanged($event)"
        [validationInProgressCaption]="'Import Change Order Template - Validation Progress'"
        [dataLoadInProgressCaption]="'Import Change Order Template - Data Load Progress'"
        class="my-8 block"
      />

      <div class="mb-[15px]">
        <div class="mb-[5px] text-lg font-bold">Change Order Budget Updates</div>
        <div>
          To update your budget based on the Change Order, start by either selecting Edit > Add New
          Activities or by selecting the existing activities you wish to modify in the table and
          clicking Edit > Edit Activities to modify them.
        </div>
      </div>

      <aux-table-panel>
        <div class="flex items-center justify-end gap-[15px] p-[15px]">
          @if (toBudgetVersion()) {
            <aux-checkbox
              id="hide_unchanged"
              class="text-sm"
              [disabled]="grid()?.showOnlyMissingActivities() || false"
              [checked]="!grid()?.showUnchangedActivities() || false"
              (customChange)="grid()?.showUnchangedActivities?.set(!$event)"
            >
              Hide Unchanged
            </aux-checkbox>
          }

          <aux-button-group
            variant="primary"
            label="Edit"
            [options]="editOptions()"
            [disabled]="!obj.isPendingReview || (hasUpdateChangeOrderPermissions$ | async) !== true"
            [disabledTooltip]="
              !obj.isPendingReview ? editButtonDisabledTooltip : doNotHavePermissionMessage
            "
            [position]="buttonGroupOverlayPosition"
          />

          <aux-button-group
            iconName="DotsVertical"
            [showDropdownIcon]="false"
            [options]="kebabOptions()"
            disabledTooltip="Please select at least one row to edit"
            [position]="buttonGroupOverlayPosition"
          />
        </div>

        @if (
          grid()?.doesBudgetDataHaveMissingActivities() === true &&
          (authQuery.adminUser$ | async) === true
        ) {
          <div
            class="m-[15px] my-4 flex justify-between rounded-md border-2 border-aux-red-dark bg-aux-error/25"
          >
            <div class="flex items-center py-4 pl-4 text-aux-red-dark">
              <aux-icon name="AlertTriangle" />
              <span class="pl-1 font-bold">
                This Change Order is missing activities that are in the current budget. Please
                review below.
              </span>
            </div>
            <aux-checkbox
              id="show_missing_activities"
              class="py-4 pr-4 text-sm"
              [checked]="!!grid()?.showOnlyMissingActivities()"
              (customChange)="grid()?.showOnlyMissingActivities?.set($event)"
            >
              Show Missing Activities
            </aux-checkbox>
          </div>
        }

        @if (fromBudgetVersion() && organization()) {
          <aux-change-order-grid
            [fromBudgetVersion]="fromBudgetVersion()!"
            [toBudgetVersion]="toBudgetVersion()!"
            [organization]="organization()!"
            [rowSelection]="rowSelection()"
            [changeOrderID]="changeOrder()!.id"
          />
        } @else {
          <div>
            <!-- todo AUXI-6868 -->
          </div>
        }
      </aux-table-panel>
    }
  }
}
